/**
 * Client State Manager for Object-Property-LWW-value system
 * Manages client identity, logical timestamps, and persistent state
 */

import type { ClientState } from '../types/shared-types';

export class ClientStateManager {
  private clientState: ClientState | null = null;
  private readonly storageKey = 'lww_client_state';

  async initialize(): Promise<ClientState> {
    if (this.clientState) {
      return this.clientState;
    }

    // Try to load existing client state from localStorage
    const stored = localStorage.getItem(this.storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ClientState;
        this.clientState = parsed;
        return this.clientState;
      } catch (error) {
        console.warn('Failed to parse stored client state, creating new one');
      }
    }

    // Generate new client state
    this.clientState = {
      user_id: this.generateUUID(),
      replica_id: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER),
      logical_timestamp: 0
    };

    // Persist to localStorage
    this.persistState();
    return this.clientState;
  }

  getClientState(): ClientState {
    if (!this.clientState) {
      throw new Error('Client state not initialized. Call initialize() first.');
    }
    return { ...this.clientState };
  }

  getNextLogicalTimestamp(): number {
    if (!this.clientState) {
      throw new Error('Client state not initialized. Call initialize() first.');
    }
    
    this.clientState.logical_timestamp++;
    this.persistState();
    return this.clientState.logical_timestamp;
  }

  getUserId(): string {
    if (!this.clientState) {
      throw new Error('Client state not initialized. Call initialize() first.');
    }
    return this.clientState.user_id;
  }

  getReplicaId(): number {
    if (!this.clientState) {
      throw new Error('Client state not initialized. Call initialize() first.');
    }
    return this.clientState.replica_id;
  }

  getCurrentLogicalTimestamp(): number {
    if (!this.clientState) {
      throw new Error('Client state not initialized. Call initialize() first.');
    }
    return this.clientState.logical_timestamp;
  }

  /**
   * Reset client state (useful for testing or switching users)
   */
  reset(): void {
    localStorage.removeItem(this.storageKey);
    this.clientState = null;
  }

  private persistState(): void {
    if (this.clientState) {
      localStorage.setItem(this.storageKey, JSON.stringify(this.clientState));
    }
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  }
}

// Singleton instance
export const clientStateManager = new ClientStateManager();
